import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { LockedScreen } from './admin/UnlockPanel';
import { authPost, clearAdminToken, getSession, isOpenPath, isUnlocked } from '../lib/adminAuth';
import {
  ClipboardList,
  CalendarDays,
  LayoutDashboard,
  Lock,
  LockOpen,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  HandCoins,
} from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(isUnlocked());
  const session = getSession();

  useEffect(() => {
    const check = () => {
      if (!getSession()) {
        clearAdminToken();
        navigate('/admin-login');
      }
    };
    check();
    const timer = setInterval(check, 60000);
    return () => clearInterval(timer);
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin-login');
  };

  const handleLock = async () => {
    try {
      await authPost('lock');
      setUnlocked(false);
      toast.success('Sections locked');
      navigate('/admin/payment-plans');
    } catch (e) {
      handleLogout();
    }
  };

  const locked = !unlocked && !isOpenPath(location.pathname);

  const menuItems = [
    {
      id: 'assessments',
      label: 'Assessments',
      icon: ClipboardList,
      path: '/admin',
      description: 'Current dashboard',
    },
    {
      id: 'new-assessments',
      label: 'New Assessments',
      icon: Sparkles,
      path: '/admin/new-assessments',
      description: 'Halaxy-synced',
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: CalendarDays,
      path: '/admin/calendar',
      description: 'Weekly view',
    },
    {
      id: 'payment-plans',
      label: 'Payment Plans',
      icon: HandCoins,
      path: '/admin/payment-plans',
      description: 'Register & follow-ups',
    },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-[#E8E4DC]">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div>
              <h2 className="text-lg font-semibold text-[#3A3530] font-heading">Navigating Minds</h2>
              <p className="text-xs text-[#9B9590]">Admin Portal</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex text-[#6B6560] hover:bg-[#F5F1E8]"
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-[#6B6560]"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                data-testid={`sidebar-nav-${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  active
                    ? 'bg-[#8B9D83] text-white shadow-sm'
                    : 'text-[#6B6560] hover:bg-[#F5F1E8] hover:text-[#3A3530]'
                }`}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-white' : 'text-[#9B9590]'}`} />
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <div>{item.label}</div>
                    {item.description && (
                      <div className={`text-xs ${active ? 'text-white/70' : 'text-[#9B9590]'}`}>
                        {item.description}
                      </div>
                    )}
                  </div>
                )}
                {!collapsed && !unlocked && !isOpenPath(item.path) && (
                  <Lock className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-white/80' : 'text-[#B5AFA8]'}`} data-testid={`sidebar-lock-icon-${item.id}`} />
                )}
              </button>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t border-[#E8E4DC] space-y-1">
        {unlocked && (
          <button
            onClick={handleLock}
            data-testid="sidebar-lock-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#6B6560] hover:bg-[#F5F1E8] transition-colors duration-150"
          >
            <LockOpen className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>Lock sections</span>}
          </button>
        )}
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#C17B6B] hover:bg-red-50 transition-colors duration-150"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex" data-testid="admin-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-[#E8E4DC] transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
        data-testid="admin-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Sidebar - Mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#E8E4DC] transform transition-transform duration-200 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-[#E8E4DC] bg-white">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            className="text-[#6B6560]"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h2 className="text-sm font-semibold text-[#3A3530]">Navigating Minds</h2>
          <div className="w-8" />
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {!session ? null : locked ? (
            <LockedScreen onUnlocked={() => setUnlocked(true)} onSessionExpired={handleLogout} />
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
};
